import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeLink } from '@/components/ui/tee-link';
import { authService } from '@/services/auth/auth.service';
import { TeeInput } from '@/components/ui/tee-input';
import { TeeDigitInput } from '@/components/ui/tee-digit-input';
import { IconBrandGoogleFilled } from '@tabler/icons-react';
import { PublicIssueBanner } from '@/components/issue-notices/PublicIssueBanner';
import { resolveLandingRoute, readStoredUser } from '@/utils/roleRouting';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;
    try {
      const { exp } = JSON.parse(atob(token.split('.')[1]));
      if (Date.now() / 1000 < exp) {
        navigate(resolveLandingRoute(readStoredUser()));
      } else {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
      }
    } catch {
      // Malformed token — clear and show login
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
    }
  }, [navigate]);

  useEffect(() => {
    if (!showOtpInput) {
      setTimeLeft(0);
      return;
    }
    setTimeLeft(600);
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setShowOtpInput(false);
          toast.error('OTP expired. Please request a new one.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [showOtpInput]);

  const handleGoogleLogin = async () => {
    try {
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      if (!clientId) {
        toast.error('Google login is not configured. Please contact support.');
        return;
      }
      const redirectUri =
        import.meta.env.VITE_GOOGLE_REDIRECT_URI ||
        `${window.location.origin}/auth/google/callback`;
      const scope = 'openid email profile';

      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.append('client_id', clientId);
      authUrl.searchParams.append('redirect_uri', redirectUri);
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('scope', scope);
      authUrl.searchParams.append('prompt', 'select_account');
      authUrl.searchParams.append('access_type', 'offline');

      window.location.href = authUrl.toString();
    } catch (error: any) {
      toast.error('Failed to initiate Google login');
    }
  };

  const requestOtp = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    try {
      setIsLoading(true);
      await authService.requestOtp(email);
      setShowOtpInput(true);
      toast.success('OTP sent to your email');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestOtp = (e: React.FormEvent) => {
    e.preventDefault();
    requestOtp();
  };

  const verifyOtp = async (code: string) => {
    if (!code) {
      toast.error('Please enter the OTP');
      return;
    }
    try {
      setIsLoading(true);
      const response = await authService.verifyOtp(email, code);
      localStorage.setItem('adminToken', response.token);
      localStorage.setItem('adminUser', JSON.stringify(response.user));
      window.dispatchEvent(new Event('authChange'));
      toast.success('Login successful!');
      navigate(resolveLandingRoute(response.user));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Invalid OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const headingCls =
    'text-heading-h1 font-extrabold uppercase tracking-wider font-[family-name:var(--type-font-family-heading)] text-[var(--text-content-title)]';
  const goldRule = <span aria-hidden className="mt-2 block h-[3px] w-16 rounded-full bg-[var(--color-gold-500)]" />;

  return (
    <div className="min-h-screen flex flex-col bg-[var(--background-page-body)]">
      <PublicIssueBanner />
      <div className="flex flex-1">
      {/* Left — forest brand panel */}
      <div className="tee-dots-surface relative hidden lg:flex lg:w-1/2 flex-col justify-between bg-[var(--color-forest)] p-14 xl:p-16">
        <img
          src="/assets/logo-white.svg"
          alt="Tee Box"
          className="relative h-11 w-[132px] object-contain object-left"
        />
        <div className="relative max-w-md">
          <h1 className="text-[52px] xl:text-[60px] leading-[1.02] font-extrabold uppercase tracking-wide font-[family-name:var(--type-font-family-heading)] text-[var(--text-on-forest)]">
            Developing every golfer
          </h1>
          <span aria-hidden className="mt-6 block h-[3px] w-16 rounded-full bg-[var(--color-gold-500)]" />
          <p className="mt-6 max-w-sm text-body-m leading-relaxed text-[var(--text-on-forest-muted)]">
            Box OS is the operating system behind every Tee Box location — bookings, members,
            assessments and fittings in one place.
          </p>
        </div>
        <span className="relative" />
      </div>

      {/* Right — sign-in panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-[400px] flex flex-col gap-6">
          {/* Logo shown here on small screens where the forest panel is hidden */}
          <img
            src="/assets/logo-white.svg"
            alt="Tee Box"
            className="lg:hidden h-10 w-[120px] object-contain object-left"
            style={{ filter: 'brightness(0)' }}
          />

          {!showOtpInput ? (
            <>
              <div>
                <h2 className={headingCls}>Sign in</h2>
                {goldRule}
                <p className="mt-3 text-body-s text-[var(--text-content-sub-title)]">
                  Use your Tee Box staff account to continue.
                </p>
              </div>

              <TeeButton
                type="button"
                variant="primary"
                size="l"
                onClick={handleGoogleLogin}
                disabled={isLoading}
                leftIcon={<IconBrandGoogleFilled size={18} />}
                className="w-full"
              >
                Continue with Google
              </TeeButton>

              <div className="flex items-center gap-4">
                <div className="flex-1 border-t border-[var(--border-default)]" />
                <span className="text-body-xs text-[var(--text-content-sub-title)]">OR</span>
                <div className="flex-1 border-t border-[var(--border-default)]" />
              </div>

              <form onSubmit={handleRequestOtp} className="flex flex-col gap-2.5">
                <label
                  htmlFor="email"
                  className="text-[11px] font-semibold uppercase tracking-widest text-[var(--text-content-sub-title)]"
                >
                  Work email
                </label>
                <TeeInput
                  id="email"
                  type="email"
                  placeholder="you@tbx.golf"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                />
                <TeeButton type="submit" variant="secondary" size="l" disabled={isLoading} className="w-full mt-1">
                  {isLoading ? 'Sending…' : 'Continue with email'}
                </TeeButton>
              </form>

              <p className="text-body-xs leading-relaxed text-[var(--text-content-sub-title)]">
                Trouble signing in? Contact your franchise administrator or{' '}
                <span className="font-semibold text-[var(--text-content-title)]">Tee Box support</span>.
              </p>
            </>
          ) : (
            <>
              <div>
                <h2 className={headingCls}>Verify code</h2>
                {goldRule}
                <p className="mt-3 text-body-s text-[var(--text-content-sub-title)]">
                  Enter the 6-digit code sent to{' '}
                  <span className="font-medium text-[var(--text-content-title)]">{email}</span>. Expires in{' '}
                  {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}.
                </p>
              </div>

              <form className="flex flex-col gap-6" onSubmit={(e) => e.preventDefault()}>
                <TeeDigitInput
                  length={6}
                  value={otp}
                  onChange={setOtp}
                  onComplete={(val) => verifyOtp(val)}
                  placeholder="•"
                />
                <p className="text-body-xs text-[var(--text-content-sub-title)]">
                  Haven't received it?{' '}
                  <TeeLink
                    variant="primary"
                    size="s"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      requestOtp();
                    }}
                  >
                    Resend a new code
                  </TeeLink>
                  .
                </p>
                <TeeButton
                  type="button"
                  variant="secondary"
                  size="l"
                  onClick={() => {
                    setShowOtpInput(false);
                    setOtp('');
                  }}
                  disabled={isLoading}
                  className="w-full"
                >
                  Back
                </TeeButton>
              </form>
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
